import { z } from 'zod'

import type { ParsedTemplateVariable } from '@/components/organisms/template-variable-editor/template-variable.interface'
import type { TemplateVariableValidatorType } from '@/components/organisms/template-variable-editor/template-variable-validator.type'

import type { TemplateVariableFormValues } from './template-variable-form.interface'

type ZodStringSchema = z.ZodString
type ZodNumberSchema = z.ZodNumber

type ConstraintApplier<T> = (schema: T, value: string) => T

const STRING_CONSTRAINT_APPLIER_MAP: Record<string, ConstraintApplier<ZodStringSchema>> = {
  minLength: (schema, value) => schema.min(Number(value), `Must be at least ${value} characters`),
  maxLength: (schema, value) => schema.max(Number(value), `Must be at most ${value} characters`),
  regex: (schema, value) => schema.regex(new RegExp(value), `Must match pattern: ${value}`),
}

const NUMBER_CONSTRAINT_APPLIER_MAP: Record<string, ConstraintApplier<ZodNumberSchema>> = {
  min: (schema, value) => schema.min(Number(value), `Must be at least ${value}`),
  max: (schema, value) => schema.max(Number(value), `Must be at most ${value}`),
}

const applyStringConstraintList = (
  schema: ZodStringSchema,
  variable: ParsedTemplateVariable
): ZodStringSchema => {
  let result = schema
  for (const constraint of variable.constraintList) {
    const applier = STRING_CONSTRAINT_APPLIER_MAP[constraint.name]
    if (applier) {
      result = applier(result, constraint.value)
    }
  }
  return result
}

const applyNumberConstraintList = (
  schema: ZodNumberSchema,
  variable: ParsedTemplateVariable
): ZodNumberSchema => {
  let result = schema
  for (const constraint of variable.constraintList) {
    const applier = NUMBER_CONSTRAINT_APPLIER_MAP[constraint.name]
    if (applier) {
      result = applier(result, constraint.value)
    }
  }
  return result
}

const buildStringSchema = (variable: ParsedTemplateVariable): ZodStringSchema => {
  const base = z.string().min(1, 'Required')
  return applyStringConstraintList(base, variable)
}

const buildUrlSchema = (variable: ParsedTemplateVariable): ZodStringSchema => {
  const base = z.string().url('Must be a valid URL').min(1, 'Required')
  return applyStringConstraintList(base, variable)
}

const buildEmailSchema = (variable: ParsedTemplateVariable): ZodStringSchema => {
  const base = z.string().email('Must be a valid email').min(1, 'Required')
  return applyStringConstraintList(base, variable)
}

const buildNumberSchema = (variable: ParsedTemplateVariable): ZodNumberSchema => {
  const base = z.coerce.number({ invalid_type_error: 'Must be a number' })
  return applyNumberConstraintList(base, variable)
}

const buildEnumSchema = (variable: ParsedTemplateVariable): z.ZodEnum<[string, ...string[]]> => {
  if (variable.enumOptionList.length === 0) {
    return z.enum([''])
  }
  return z.enum(variable.enumOptionList as [string, ...string[]])
}

type SchemaBuilderFn = (variable: ParsedTemplateVariable) => z.ZodTypeAny

const SCHEMA_BUILDER_MAP: Record<TemplateVariableValidatorType, SchemaBuilderFn> = {
  text: buildStringSchema,
  url: buildUrlSchema,
  email: buildEmailSchema,
  number: buildNumberSchema,
  enum: buildEnumSchema,
}

/**
 * Builds a dynamic Zod schema from a list of parsed template variables.
 * Each variable name becomes a field key; schema rules derive from the variable type and constraints.
 */
export const buildTemplateVariableSchema = (
  variableList: ParsedTemplateVariable[]
): z.ZodObject<Record<string, z.ZodTypeAny>> => {
  const shape: Record<string, z.ZodTypeAny> = {}

  for (const variable of variableList) {
    const builder = SCHEMA_BUILDER_MAP[variable.type]
    shape[variable.name] = builder(variable)
  }

  return z.object(shape)
}

const DEFAULT_VALUE_MAP: Record<TemplateVariableValidatorType, string | number> = {
  text: '',
  url: '',
  email: '',
  number: 0,
  enum: '',
}

/**
 * Builds default form values from a list of parsed template variables.
 * Uses the variable's `defaultValue` if set, otherwise falls back to type-based defaults.
 */
export const buildTemplateVariableDefaultValues = (
  variableList: ParsedTemplateVariable[]
): TemplateVariableFormValues => {
  const values: TemplateVariableFormValues = {}

  for (const variable of variableList) {
    if (variable.defaultValue !== null) {
      values[variable.name] =
        variable.type === 'number' ? Number(variable.defaultValue) : variable.defaultValue
      continue
    }

    if (variable.type === 'enum' && variable.enumOptionList.length > 0) {
      values[variable.name] = variable.enumOptionList[0]
      continue
    }

    values[variable.name] = DEFAULT_VALUE_MAP[variable.type]
  }

  return values
}
